import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ExhaustedHeroSection from '@/components/ExhaustedHeroSection';
import TestimonialSection from '@/components/TestimonialSection';
import PersonaToggle from '@/components/PersonaToggle';
import AccountabilitySection from '@/components/AccountabilitySection';
import FeatureShowcase from '@/components/FeatureShowcase';
import AllInOnePlatform from '@/components/AllInOnePlatform';
import OutputGallery from '@/components/OutputGallery';
import MoatSection from '@/components/MoatSection';
import CompetitorComparison from '@/components/CompetitorComparison';
import PricingSection from '@/components/PricingSection';
import UrgencySection from '@/components/UrgencySection';
import SecurityFooter from '@/components/SecurityFooter';
import OptimizedOnboardingV2 from '@/components/OptimizedOnboardingV2';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Check, Star, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  trackPageView, 
  trackConversion, 
  trackEngagement, 
  setupScrollTracking 
} from '@/utils/analytics';

const ExhaustedLanding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const navigate = useNavigate();
  
  // Track page view and set up scroll tracking on component mount
  useEffect(() => {
    // Track page view
    trackPageView('Exhausted Marketer Landing', '/exhausted');
    
    // Set up scroll depth tracking
    const cleanup = setupScrollTracking('exhausted');
    
    // Clean up scroll tracking on unmount
    return cleanup;
  }, []);

  // Handler for sign in clicks
  const handleSignInClick = () => {
    trackEngagement('click_sign_in', { page: 'exhausted' });
    navigate('/login');
  };
  
  // Handler for starting the trial
  const handleStartTrial = () => {
    trackConversion('start_trial', { source: 'exhausted' });
    setShowOnboarding(true);
  };
  
  // Handler for booking a demo
  const handleBookDemo = () => {
    trackConversion('book_demo', { source: 'exhausted' });
    navigate('/book-demo');
  };

  // Optimized pricing section
  const PricingSection = () => (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-800">
            Simple Pricing
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start with a 3-day free trial. No setup fees, no contracts. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Growth Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-blue-50 border-2 border-blue-500 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 relative"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-blue-600 text-white">
                <Crown className="w-3 h-3 mr-1" />
                Most Popular
              </Badge>
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Growth</h3>
              <div className="text-4xl font-bold text-blue-600 mb-1">$97</div>
              <div className="text-sm text-gray-600 mb-2">$0 today → $97 after 3-day trial</div>
              <p className="text-gray-600 mb-6">For scaling businesses</p>
              
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">150,000 emails/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">AI-powered campaigns</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Automated lead sourcing</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Meeting booking</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Email automation</span>
                </li>
              </ul>
              
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  trackConversion('select_growth_plan', { page: 'exhausted' });
                  handleStartTrial();
                }}
              >
                Start 3-Day Trial
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro</h3>
              <div className="text-4xl font-bold text-gray-900 mb-4">$297</div>
              <p className="text-gray-600 mb-6">For high-volume senders</p>
              
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">500,000 emails/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Everything in Growth</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Advanced AI optimization</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">White-label options</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Dedicated support</span>
                </li>
              </ul>
              
              <Button 
                variant="outline" 
                className="w-full border-gray-300"
                onClick={() => {
                  trackConversion('select_pro_plan', { page: 'exhausted' });
                  handleStartTrial();
                }}
              >
                Start Pro Trial
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Guarantee */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-50 border border-green-200 rounded-full">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">
              30-day money-back guarantee • Cancel anytime • No setup fees
            </span>
          </div>
        </div>
      </div>
    </section>
  );

  // FAQ Section
  const FAQSection = () => (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about Munger.ai
          </p>
        </div>

        <div className="space-y-6">
          {[
            {
              q: "How quickly can I start seeing results?",
              a: "Most users see their first replies within 24-48 hours and book their first meeting within 3-5 days of going live."
            },
            {
              q: "Do I need to provide my own email lists?",
              a: "No! Our AI automatically finds and researches prospects based on your ideal customer profile. No lists required."
            },
            {
              q: "What if I don't get results?",
              a: "We offer a 30-day money-back guarantee. If you're not getting meetings booked, we'll refund your subscription completely."
            },
            {
              q: "How is this different from other cold email tools?",
              a: "Other tools require manual setup, list building, and copywriting. Munger.ai is 100% automated - it finds prospects, writes emails, sends campaigns, and books meetings without any manual work."
            },
            {
              q: "Is my data secure?",
              a: "Yes. We use enterprise-grade security with SOC 2 compliance. Your data is encrypted and never shared with third parties."
            }
          ].map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              onClick={() => trackEngagement('faq_click', { question: faq.q, page: 'exhausted' })}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {faq.q}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {faq.a}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Final CTA Section
  const FinalCTASection = () => (
    <section className="py-20 bg-blue-600">
      <div className="max-w-4xl mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Sleeping Again?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join 2,100+ companies using AI to scale their outreach. Setup takes 10 minutes.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button 
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              onClick={() => {
                trackConversion('final_cta_trial', { page: 'exhausted' });
                handleStartTrial();
              }}
            >
              Start 3-Day Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <Button 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold"
              onClick={handleBookDemo}
            >
              Book Demo
            </Button>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-blue-100">
              Already have an account?{' '}
              <Button 
                variant="link"
                className="p-0 h-auto text-sm underline hover:text-white transition-colors"
                onClick={handleSignInClick}
              >
                Sign in
              </Button>
            </p>
          </div>
          
          <div className="flex items-center justify-center gap-6 text-sm text-blue-100">
            <span>✓ 10-minute setup</span>
            <span>✓ First replies in 24 hours</span>
            
          </div>
        </motion.div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <ExhaustedHeroSection onStartClick={handleStartTrial} />
      
      {/* Testimonials - RIGHT AFTER HERO for maximum impact */}
      <TestimonialSection />
      
      {/* Urgency Section - Create FOMO early */}
      <UrgencySection onStartClick={handleStartTrial} />
      
      {/* Persona Toggle */}
      <PersonaToggle />
      
      {/* Accountability Section - Key differentiator */}
      <AccountabilitySection />
      
      {/* How It Works - Process Showcase */}
      <FeatureShowcase />
      
      {/* ROI Gallery - Show real results */}
      <OutputGallery />
      
      {/* All-in-One Platform - Consolidated Features */}
      <AllInOnePlatform onStartClick={handleStartTrial} />
      
      {/* Competitor Comparison */}
      <CompetitorComparison onStartClick={handleStartTrial} />
      
      {/* Trust/Authority Section */}
      <MoatSection />
      
      {/* Pricing */}
      <PricingSection />
      
      {/* FAQ Section */}
      <FAQSection />
      
      {/* Final CTA */}
      <FinalCTASection />
      
      {/* Security Footer */}
      <SecurityFooter />
      
      {/* Optimized Onboarding Modal */}
      <OptimizedOnboardingV2 
        isOpen={showOnboarding} 
        onClose={() => {
          trackEngagement('close_onboarding', { page: 'exhausted' });
          setShowOnboarding(false);
        }}
        onComplete={(data) => {
          trackConversion('complete_onboarding', { page: 'exhausted', ...data });
        }}
      />
    </div>
  );
};

export default ExhaustedLanding; 